<template>
  <div class="balance-change-log">
    <div class="log-header">
      <div class="log-title">{{ $t('balanceLog.title') || '余额变动记录' }}</div>
      <button class="refresh-btn" @click="fetchLogs">
        <i class="el-icon-refresh" :class="{ 'rotating': loading }"></i>
      </button>
    </div>

    <!-- 账户类型选择 -->
    <div class="account-type-tabs">
      <div 
        v-for="type in accountTypes" 
        :key="type.value"
        class="tab-item"
        :class="{ 'active': selectedAccountType === type.value }"
        @click="selectAccountType(type.value)"
      >
        <i :class="type.icon"></i>
        <span>{{ $t(type.nameKey) || type.name }}</span>
      </div>
    </div>

    <!-- 加载中 -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <span>{{ $t('common.loading') || '加载中...' }}</span>
    </div>

    <!-- 记录列表 -->
    <div v-else-if="logs.length > 0" class="logs-list">
      <div v-for="log in logs" :key="log.id" class="log-item">
        <div class="log-main">
          <div class="log-info">
            <div class="log-type">
              <span class="type-name">{{ getTypeName(log.changeType, log.remark) }}</span>
            </div>
            <div class="log-time">{{ formatTime(log.createTime) }}</div>
          </div>
          <div class="log-amount" :class="{ 'positive': log.changeAmount > 0, 'negative': log.changeAmount < 0 }">
            {{ log.changeAmount > 0 ? '+' : '' }}{{ formatNumber(log.changeAmount) }} USD
          </div>
        </div>
        <div class="log-details">
          <div class="detail-row" v-if="log.businessId">
            <span class="label">{{ $t('balanceLog.orderId') || '订单ID' }}:</span>
            <span class="value order-id">{{ log.businessId }}</span>
          </div>
          <div class="detail-row">
            <span class="label">{{ $t('balanceLog.beforeBalance') || '变动前' }}:</span>
            <span class="value">{{ formatNumber(log.beforeBalance) }} USD</span>
          </div>
          <div class="detail-row">
            <span class="label">{{ $t('balanceLog.afterBalance') || '变动后' }}:</span>
            <span class="value">{{ formatNumber(log.afterBalance) }} USD</span>
          </div>
        </div>
      </div>

      <!-- 加载更多 -->
      <div v-if="hasMore" class="load-more" @click.stop="loadMore" :class="{ 'loading': loadingMore }">
        <i v-if="loadingMore" class="el-icon-loading"></i>
        <span>{{ loadingMore ? ($t('common.loading') || '加载中...') : ($t('common.loadMore') || '加载更多') }}</span>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-state">
      <i class="el-icon-document"></i>
      <p>{{ $t('balanceLog.noRecords') || '暂无记录' }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BalanceChangeLog',
  props: {
    accountType: {
      type: Number,
      default: null // null表示显示所有账户类型选择
    }
  },
  data() {
    return {
      selectedAccountType: null,
      logs: [],
      loading: false,
      loadingMore: false, // 加载更多时的loading状态
      pageNum: 1,
      pageSize: 20,
      total: 0,
      accountTypes: [
        {
          value: null,
          name: '全部',
          nameKey: 'balanceLog.allAccounts',
          icon: 'el-icon-s-grid'
        },
        {
          value: 1,
          name: '普通账户',
          nameKey: 'transfer.spotAccount',
          icon: 'el-icon-wallet'
        },
        {
          value: 2,
          name: '合约账户',
          nameKey: 'transfer.contractAccount',
          icon: 'el-icon-files'
        },
        {
          value: 3,
          name: '期权账户',
          nameKey: 'transfer.optionAccount',
          icon: 'el-icon-document-checked'
        }
      ]
    };
  },
  computed: {
    hasMore() {
      return this.logs.length < this.total;
    }
  },
  mounted() {
    this.selectedAccountType = this.accountType;
    this.fetchLogs();
  },
  methods: {
    async fetchLogs(append = false) {
      if (!append) {
        this.pageNum = 1;
        this.logs = [];
      }
      
      // 如果是追加加载，使用 loadingMore，否则使用 loading
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      
      try {
        const params = {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        };
        
        if (this.selectedAccountType !== null) {
          params.accountType = this.selectedAccountType;
        }
        
        const res = await this.$http.get('/swx/customer/account/balance/logs', { params });
        
        if (res.code === 200 && res.result) {
          const newLogs = res.result.list || [];
          this.total = res.result.total || 0;
          
          if (append) {
            this.logs = [...this.logs, ...newLogs];
          } else {
            this.logs = newLogs;
          }
        }
      } catch (error) {
        console.error('获取余额变动记录失败:', error);
        this.$toast.error(this.$t('balanceLog.fetchError') || '获取记录失败');
      } finally {
        if (append) {
          this.loadingMore = false;
        } else {
          this.loading = false;
        }
      }
    },
    
    async loadMore() {
      // 如果正在加载，防止重复点击
      if (this.loadingMore) {
        return;
      }
      
      this.pageNum++;
      await this.fetchLogs(true);
    },
    
    selectAccountType(type) {
      this.selectedAccountType = type;
      this.fetchLogs();
    },
    
    getTypeName(type, remark = '') {
      // 判断是否为合约、期权或理财业务
      const isContract = remark && remark.includes('合约');
      const isOption = remark && remark.includes('期权');
      const isFinancial = remark && remark.includes('理财');
      const isCancel = remark && remark.includes('撤单');
      
      // ✅ 爆仓相关的细分判断
      const isExcessLoss = remark && remark.includes('爆仓-超额亏损'); // 超额亏损
      const isMarginLoss = remark && remark.includes('爆仓-保证金亏损'); // 保证金亏损
      const isLiquidationProfit = remark && remark.includes('爆仓平仓-盈利'); // 爆仓时的盈利订单
      const isLiquidationLoss = remark && remark.includes('爆仓平仓-亏损'); // 爆仓时的亏损订单
      const isLiquidation = remark && (remark.includes('爆仓') || remark.includes('liquidation')) && !remark.includes('强制平仓');
      
      // ✅ 判断是否为系统强制平仓
      const isForcedClose = remark && remark.includes('强制平仓');
      
      const typeMap = {
        1: this.$t('balanceLog.type.deposit') || '充值',
        2: this.$t('balanceLog.type.withdraw') || '提现',
        3: this.$t('balanceLog.type.transferIn') || '转入',
        4: this.$t('balanceLog.type.transferOut') || '转出',
        5: (isContract || isOption) ? (this.$t('balanceLog.type.openPosition') || '开仓') : 
           isFinancial ? (this.$t('balanceLog.type.financialBuy') || '理财买入') : 
           (this.$t('balanceLog.type.tradeBuy') || '买入'),
        6: isContract ? (this.$t('balanceLog.type.closePosition') || '平仓') : 
           isFinancial ? (this.$t('balanceLog.type.financialReturn') || '理财返还') :
           (this.$t('balanceLog.type.tradeSell') || '卖出'),
        7: isLiquidationProfit ? (this.$t('balanceLog.type.liquidationProfit') || '爆仓平仓盈利') :
           isForcedClose ? (this.$t('balanceLog.type.closePosition') || '平仓') :
           isOption ? (this.$t('balanceLog.type.closePosition') || '平仓') : 
           isFinancial ? (this.$t('balanceLog.type.financialProfit') || '理财收益') :
           (this.$t('balanceLog.type.profit') || '盈利'),
        8: isExcessLoss ? (this.$t('balanceLog.type.excessLoss') || '爆仓超额亏损') :
           isMarginLoss ? (this.$t('balanceLog.type.marginLoss') || '爆仓保证金亏损') :
           isLiquidationLoss ? (this.$t('balanceLog.type.liquidationLoss') || '爆仓平仓亏损') :
           isLiquidation ? (this.$t('balanceLog.type.liquidation') || '爆仓') :
           isForcedClose ? (this.$t('balanceLog.type.closePosition') || '平仓') :
           (this.$t('balanceLog.type.loss') || '亏损'),
        9: this.$t('balanceLog.type.loan') || '贷款',
        10: this.$t('balanceLog.type.repayment') || '还款',
        11: this.$t('balanceLog.type.interest') || '利息',
        12: isCancel ? (this.$t('balanceLog.type.feeRefund') || '手续费退回') : (this.$t('balanceLog.type.fee') || '手续费'),
        13: this.$t('balanceLog.type.bonus') || '奖励',
        14: isCancel ? (this.$t('balanceLog.type.cancelRefund') || '撤单退回') : (this.$t('balanceLog.type.adjustment') || '调整'),
        15: this.$t('balanceLog.type.marginDeduct') || '保证金',
        16: this.$t('balanceLog.type.marginReturn') || '保证金'
      };
      return typeMap[type] || this.$t('balanceLog.type.other') || '其他';
    },
    
    getTypeIcon(type) {
      const iconMap = {
        1: 'el-icon-download',
        2: 'el-icon-upload2',
        3: 'el-icon-bottom',
        4: 'el-icon-top',
        5: 'el-icon-sell',
        6: 'el-icon-sold-out',
        7: 'el-icon-success',
        8: 'el-icon-error',
        9: 'el-icon-bank-card',
        10: 'el-icon-money',
        11: 'el-icon-coin',
        12: 'el-icon-document-remove',
        13: 'el-icon-present',
        14: 'el-icon-edit',
        15: 'el-icon-s-claim',
        16: 'el-icon-s-release'
      };
      return iconMap[type] || 'el-icon-document';
    },
    
    getTypeColor(type) {
      const colorMap = {
        1: '#52c41a',
        2: '#ff4d4f',
        3: '#1890ff',
        4: '#faad14',
        5: '#f5222d',
        6: '#52c41a',
        7: '#52c41a',
        8: '#ff4d4f',
        9: '#722ed1',
        10: '#13c2c2',
        11: '#fadb14',
        12: '#fa8c16',
        13: '#eb2f96',
        14: '#2f54eb',
        15: '#fa8c16',
        16: '#1890ff'
      };
      return colorMap[type] || '#999';
    },
    
    formatNumber(num) {
      return parseFloat(num || 0);
    },
    
    translateRemark(remark) {
      if (!remark) return '';
      // 尝试从翻译文件中获取翻译
      const translationKey = `balanceLog.remarks.${remark}`;
      const translated = this.$t(translationKey);
      // 如果翻译存在且不等于key本身，返回翻译；否则返回原文
      return translated !== translationKey ? translated : remark;
    },
    
    formatTime(time) {
      if (!time) return '';
      const date = new Date(time);
      
      // 统一显示完整的日期和时间：YYYY-MM-DD HH:mm:ss
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
  }
};
</script>

<style scoped>
.balance-change-log {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 20px;
  transition: background-color 0.3s ease;
}

/* 头部 */
.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.log-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.refresh-btn {
  padding: 8px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  background: var(--hover-bg);
  border-color: var(--primary-color, #0156DC);
  color: var(--primary-color, #0156DC);
}

.refresh-btn i {
  font-size: 16px;
}

.refresh-btn i.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 账户类型选择 */
.account-type-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 5px;
}

.account-type-tabs::-webkit-scrollbar {
  height: 4px;
}

.account-type-tabs::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 2px;
}

.tab-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--bg-secondary);
  border: none; /* ✅ 删除边框 */
  border-radius: 20px;
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.tab-item:hover {
  background: var(--hover-bg);
}

.tab-item.active {
  background: var(--primary-color-10);
  /* ✅ 删除边框颜色 */
  color: var(--primary-color, #0156DC);
  font-weight: 600;
}

.tab-item i {
  font-size: 16px;
}

/* 加载中 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-secondary);
}

.loading-container i {
  font-size: 32px;
  margin-bottom: 10px;
  animation: rotate 1s linear infinite;
}

/* 记录列表 */
.logs-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.log-item {
  padding: 15px;
  background: var(--bg-secondary);
  border-radius: 10px;
  /* ✅ 删除边框 */
  transition: all 0.3s ease;
}

.log-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.log-info {
  flex: 1;
}

.log-type {
  margin-bottom: 5px;
}

.type-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.log-time {
  font-size: 12px;
  color: var(--text-secondary);
}

.log-amount {
  font-size: 18px;
  font-weight: 700;
}

.log-amount.positive {
  /* ✅ 使用主题色 */
  color: var(--primary-color, #0156DC);
}

.log-amount.negative {
  color: #ff4d4f;
}

.log-details {
  padding-top: 10px;
  /* ✅ 删除顶部虚线边框 */
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  font-size: 13px;
}

.detail-row .label {
  color: var(--text-secondary);
}

.detail-row .value {
  color: var(--text-primary);
  font-weight: 500;
}

.detail-row .value.order-id {
  /* ✅ 使用和其他字段相同的字体 */
  color: var(--primary-color, #0156DC);
  word-break: break-all;
}

/* 加载更多 */
.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 15px;
  color: var(--primary-color, #0156DC);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.load-more:hover:not(.loading) {
  background: var(--hover-bg);
  border-radius: 8px;
}

.load-more.loading {
  cursor: not-allowed;
  opacity: 0.6;
}

.load-more.loading i {
  animation: rotate 1s linear infinite;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
}

.empty-state i {
  font-size: 64px;
  color: var(--border-color);
  margin-bottom: 15px;
}

.empty-state p {
  font-size: 14px;
  margin: 0;
}

/* 响应式 */
@media (max-width: 768px) {
  .balance-change-log {
    padding: 15px;
  }

  .log-title {
    font-size: 16px;
  }

  .log-item {
    padding: 12px;
  }

  .log-amount {
    font-size: 16px;
  }
}

/* 黑夜模式适配 */
.dark-mode .log-item {
  background: rgba(255, 255, 255, 0.05);
}

.dark-mode .tab-item {
  background: rgba(255, 255, 255, 0.05);
}

.dark-mode .tab-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.dark-mode .tab-item.active {
  background: rgba(var(--primary-color-rgb), 0.2);
}
</style>

